import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { X, Plus } from 'lucide-react';
import FormInput from '../../../shared/components/ui/FormInput';

const ACCENT = '#5FB4C4';

const schema = yup.object().shape({
  campaignName: yup.string().trim().required('Campaign name is required'),
  modalProject: yup.string().required('Select a project'),
  description: yup.string().trim().default('')
});

const CreateWhatsAppCampaignModal = ({
  open,
  onClose,
  onCreate,
  projectsForDropdown,
  defaultProjectId
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      campaignName: '',
      modalProject: '',
      description: ''
    }
  });

  useEffect(() => {
    if (!open) return;
    reset({
      campaignName: '',
      modalProject: defaultProjectId != null && defaultProjectId !== '' ? String(defaultProjectId) : '',
      description: ''
    });
  }, [open, defaultProjectId, reset]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (values) => {
    await onCreate({
      campaignName: values.campaignName.trim(),
      projectId: values.modalProject,
      description: values.description?.trim() || ''
    });
  };

  return (
    <div
      className="fixed inset-0 z-[9050] flex items-center justify-center bg-black/30 px-4 py-8"
      onClick={onClose}
      role="presentation">
      <div
        className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-campaign-modal-title">
        <header className="flex items-start justify-between gap-4 bg-[#E8F4F6] px-6 py-4">
          <div className="min-w-0">
            <h2
              id="create-campaign-modal-title"
              className="text-lg font-bold text-gray-900">
              Create New Campaign
            </h2>
            <p className="mt-0.5 text-sm text-gray-500">Set up your WhatsApp Lead Flow</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1 text-gray-600 transition-colors hover:bg-white/60 hover:text-gray-900"
            aria-label="Close">
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </header>

        <form onSubmit={handleSubmit(submit)} className="flex flex-col">
          <div className="space-y-4 px-6 py-6">
            <FormInput
              width={100}
              label="Campaign Name"
              register={register}
              register_key="campaignName"
              isRequired
              type="textOnType"
              placeholder="e.g., Green Heights"
              errors={errors.campaignName}
            />

            <FormInput
              width={100}
              label="Project Name"
              register={register}
              register_key="modalProject"
              isRequired
              type="dropdown"
              options={[
                { label: 'Select', value: '', disabled: true },
                ...(projectsForDropdown || [])
              ]}
              errors={errors.modalProject}
            />

            <FormInput
              width={100}
              label="Description"
              register={register}
              register_key="description"
              isRequired={false}
              type="textarea"
              placeholder="Type here..."
              errors={errors.description}
            />

            <p className="text-sm leading-relaxed text-gray-500">
              After creating your campaign, you&apos;ll be able to add questions, greeting messages, and
              configure the lead flow.
            </p>
          </div>

          <footer className="flex items-center justify-between gap-3 border-t border-gray-200 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border-2 px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-gray-50"
              style={{ borderColor: ACCENT, color: ACCENT }}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: ACCENT }}>
              <Plus className="h-4 w-4" strokeWidth={2.5} />
              Create Campaign
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default CreateWhatsAppCampaignModal;
